import { Component, ErrorInfo, ReactNode } from 'react';
import { en } from '../../context/translations/en';
import { ar } from '../../context/translations/ar';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

// This boundary wraps <App> (which itself renders <LanguageProvider>), so if
// the app crashes above or inside the provider, useLanguage() isn't reachable
// here — read the persisted preference directly instead (same localStorage
// key/dictionaries LanguageContext uses) rather than the hook.
const t = (key: string): string => {
  let lang: "en" | "ar" = "en";
  try {
    if (localStorage.getItem("arteria_language") === "ar") lang = "ar";
  } catch { /* localStorage unavailable */ }
  const store = lang === "ar" ? ar : en;
  const val = store[key];
  return typeof val === "string" ? val : key;
};

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold text-red-600 mb-4">{t("common.somethingWentWrong")}</h2>
            <div className="bg-red-50 border border-red-200 rounded p-4 mb-4">
              <p className="text-sm text-red-800">
                {this.state.error?.message || t("errorBoundary.unexpectedError")}
              </p>
            </div>
            <button
              onClick={() => window.location.reload()}
              className="w-full bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700 transition-colors"
            >
              {t("errorBoundary.reloadPage")}
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;